// This file is generated by [oats][0] and should not be edited by hand.
//
// [0]: https://github.com/influxdata/oats

export interface Me {
  id: string
  email: string
  accountType: AccountType
  billingProvider: 'zuora' | 'aws' | 'gcm' | 'azure'
  clusterHost: string
  isRegionBeta: boolean
  regionCode: string
  regionName: string
  isOperator: boolean
  operatorRole?: 'read-only' | 'read-write'
  accountCreatedAt: string
  paygCreditStartDate?: string
}

export type AccountType = 'cancelled' | 'contract' | 'free' | 'pay_as_you_go'

export interface Error {
  readonly code:
    | 'internal error'
    | 'not found'
    | 'conflict'
    | 'invalid'
    | 'unprocessable entity'
    | 'empty value'
    | 'unavailable'
    | 'forbidden'
    | 'too many requests'
    | 'unauthorized'
    | 'method not allowed'
    | 'request too large'
    | 'unsupported media type'
  readonly message?: string
  readonly op?: string
  readonly err?: string
}

export interface User {
  email?: string
  firstName: string
  id: string
  idpeId: string
  lastName: string
}

export interface UserAccount {
  id: number
  isActive: boolean
  isDefault: boolean
  name: string
}

export interface Account {
  id: number
  name: string
  type?: 'contract' | 'free' | 'pay_as_you_go'
  billingProvider?: 'aws' | 'azure' | 'gcm' | 'zuora'
}

export type OrganizationSummaries = Array<{
  id: string
  name: string
  isActive: boolean
  isDefault: boolean
}>

export interface OrganizationDefaultRequest {
  id: string
}

export interface BillingInfo {
  balance: number
  region?: string
  paymentMethod?: PaymentMethod
  balanceUpdatedAt: string
  contact: BillingContact
}

export interface PaymentMethod {
  cardType: string
  cardNumber: string
  expirationMonth: string
  expirationYear: string
  defaultPaymentMethod?: boolean
}

export interface BillingContact {
  companyName: string
  email: string
  firstName: string
  lastName: string
  country: string
  street1?: string
  street2?: string
  city: string
  subdivision?: string
  postalCode: string
}

export interface Identity {
  user: IdentityUser
  account: IdentityAccount
  org: IdentityOrganization
}

export interface IdentityUser {
  id: string
  email: string
  firstName?: string
  lastName?: string
  operatorRole?: 'read-only' | 'read-write'
  accountCount: number
  orgCount: number
}

export interface IdentityAccount {
  id: number
  name: string
  type: AccountType
  accountCreatedAt: string
  isUpgradeable?: boolean
  paygCreditStartDate?: string
}

export interface IdentityOrganization {
  id: string
  name: string
  clusterHost: string
}

export interface Marketplace {
  name: string
  shortName: MarketplaceType
  url: string
}

export type MarketplaceType = 'aws' | 'gcm' | 'azure'

export interface BillingDate {
  dateTime: string
}

export type UsageVectors = UsageVector[]

export interface UsageVector {
  name: string
  unit: string
  fluxKey: string
}

export interface BillingNotifySettings {
  isNotify: boolean
  balanceThreshold: number
  notifyEmail: string
}

export interface CreditCardParams {
  readonly id: string
  readonly tenantId: string
  readonly key: string
  readonly signature: string
  readonly token: string
  readonly style: string
  readonly submitEnabled: 'true' | 'false'
  readonly url: string
}

export interface PaymentMethodPut {
  readonly paymentMethodId?: string
}

export type Invoices = Invoice[]

export interface Invoice {
  id: string
  readonly status: string
  readonly amount: number
  readonly targetDate: string
}

export interface Organization {
  id: string
  name?: string
  description?: string
  creationDate?: string
  regionCode: string
  regionName: string
  provider?: string
  isRegionBeta: boolean
  clusterHost: string
}

export type Invites = Invite[]

export interface Invite {
  readonly id?: number
  email: string
  role: Role
  readonly expiresAt?: string
}

export type Role = 'owner' | 'member'

export interface OrgUser {
  readonly id: string
  firstName: string
  lastName: string
  email: string
  role: Role
}

export type OrgUsers = OrgUser[]

export interface OrgLimits {
  readonly orgID: string
  rate: RateLimits
  bucket: BucketLimits
  task: TaskLimits
  dashboard: DashboardLimits
  check: CheckLimits
  notificationRule: NotificationRuleLimits
  notificationEndpoint: NotificationEndpointLimits
}

export interface RateLimits {
  readKBs?: RestrictedLimit | Limit
  writeKBs?: RestrictedLimit | Limit
  cardinality?: RestrictedLimit | Limit
}

export type RestrictedLimit = number

export type Limit = number

export interface BucketLimits {
  maxBuckets?: RestrictedLimit | Unlimited | Limit
  maxRetentionDuration?: RestrictedLimit | Unlimited | Limit
}

export type Unlimited = number

export interface TaskLimits {
  maxTasks?: RestrictedLimit | Unlimited | Limit
}

export interface DashboardLimits {
  maxDashboards?: RestrictedLimit | Unlimited | Limit
}

export interface CheckLimits {
  maxChecks?: RestrictedLimit | Unlimited | Limit
}

export interface NotificationRuleLimits {
  maxNotifications?: RestrictedLimit | Unlimited | Limit
  blockedNotificationRules?: string
}

export interface NotificationEndpointLimits {
  blockedNotificationEndpoints?: string
}

export interface CheckoutRequest {
  paymentMethodId?: string
  street1?: string
  street2?: string
  city?: string
  country?: string
  subdivision?: string
  postalCode?: string
  notifyEmail?: string
  balanceThreshold?: number
  isNotify?: boolean
  isPaygCreditActive?: boolean
}

export type OperatorAccounts = OperatorAccount[]

export interface OperatorAccount {
  id: number
  name: string
  type: AccountType
  zuoraAccountId?: string
  deletable?: boolean
  balance: number
  billingContact: BillingContact
  users: OperatorUsers
  marketplaceSubscription: MarketplaceSubscription
}

export type OperatorUsers = OperatorUser[]

export interface OperatorUser {
  email?: string
  firstName: string
  id: string
  idpeId: string
  lastName: string
  operator?: boolean
  sfdcContactId?: string
}

export interface MarketplaceSubscription {
  marketplace: MarketplaceType
  status: 'pending' | 'subscribed' | 'unsubscribed' | 'failed'
  subscriberId: string
}

export type OperatorOrganizations = OperatorOrganization[]

export interface OperatorOrganization {
  idpeId: string
  id: number
  name?: string
  region: string
  provider: string
  date: string
  account: RelatedAccount
}

export interface RelatedAccount {
  id: number
  type: AccountType
  balance: number
  billingContact?: BillingContact
  updatedAt: string
}

export interface OperatorProvidersResponse {
  providers: OperatorProviders
  regions: {
    Azure?: OperatorRegions
    AWS?: OperatorRegions
    GCM?: OperatorRegions
  }
}

export type OperatorProviders = OperatorProvider[]

export interface OperatorProvider {
  provider: string
  title: string
  isAvailable: boolean
}

export type OperatorRegions = OperatorRegion[]

export interface OperatorRegion {
  region: string
  title: string
  provider: string
  priority: number
  isAvailable: boolean
  isBeta: boolean
  isPrivate: boolean
}

export interface OperatorOrgLimits {
  readonly orgID: string
  rate: OperatorRateLimits
  bucket: BucketLimits
  task: TaskLimits
  dashboard: DashboardLimits
  check: CheckLimits
  notificationRule: NotificationRuleLimits
  notificationEndpoint: NotificationEndpointLimits
}

export interface OperatorRateLimits {
  queryTime?: RestrictedLimit | Limit
  readKBs?: RestrictedLimit | Limit
  writeKBs?: RestrictedLimit | Limit
  cardinality?: RestrictedLimit | Limit
}

interface RequestOptions {
  signal?: AbortSignal
}

export type RequestHandler = (
  url: string,
  query: string,
  init: RequestInit
) => {url: string; query: string; init: RequestInit}
export type ResponseHandler = (
  status: number,
  headers: Headers,
  data: any
) => {status: number; headers: Headers; data: any}

const RequestContext = function(
  requestHandler: RequestHandler,
  responseHandler: ResponseHandler
) {
  this.requestHandler = requestHandler
  this.responseHandler = responseHandler
}

RequestContext.prototype.request = async function(
  method: string,
  url: string,
  params: any = {},
  options: RequestOptions = {}
): Promise<any> {
  const requestHeaders = new Headers(params.headers)
  const contentType = requestHeaders.get('Content-Type') || ''

  if (params.auth) {
    const credentials = btoa(`${params.auth.username}:${params.auth.password}`)

    requestHeaders.append('Authorization', `Basic ${credentials}`)
  }

  const body =
    params.data && contentType.includes('json')
      ? JSON.stringify(params.data)
      : params.data

  const query = params.query ? `?${new URLSearchParams(params.query)}` : ''

  const {
    url: middlewareUrl,
    query: middlewareQuery,
    init,
  } = this.requestHandler(url, query, {
    method,
    body,
    credentials: 'same-origin',
    signal: options.signal,
    headers: requestHeaders,
  })

  const response = await fetch(`${middlewareUrl}${middlewareQuery}`, init)

  const {status, headers} = response
  const responseContentType = headers.get('Content-Type') || ''

  let data

  if (responseContentType.includes('json')) {
    data = await response.json()
  } else if (responseContentType.includes('octet-stream')) {
    data = await response.blob()
  } else {
    data = await response.text()
  }

  return this.responseHandler(status, headers, data)
}

RequestContext.prototype.setRequestHandler = function(
  requestHandler: RequestHandler
) {
  this.requestHandler = requestHandler
}

RequestContext.prototype.setResponseHandler = function(
  responseHandler: ResponseHandler
) {
  this.responseHandler = responseHandler
}

const rc = new RequestContext(
  (url, query, init) => {
    return {url, query, init}
  },
  (status, headers, data) => {
    return {status, headers, data}
  }
)
const request = rc.request.bind(rc)
const setRequestHandler = rc.setRequestHandler.bind(rc)
const setResponseHandler = rc.setResponseHandler.bind(rc)

export {request, setRequestHandler, setResponseHandler}

export interface GetMeParams {}

type GetMeResult =
  | GetMeOKResult
  | GetMeUnauthorizedResult
  | GetMeNotFoundResult
  | GetMeDefaultResult

interface GetMeOKResult {
  status: 200
  headers: Headers
  data: Me
}

interface GetMeUnauthorizedResult {
  status: 401
  headers: Headers
  data: Error
}

interface GetMeNotFoundResult {
  status: 404
  headers: Headers
  data: Error
}

interface GetMeDefaultResult {
  status: 500
  headers: Headers
  data: Error
}

export const getMe = (
  params: GetMeParams,
  options: RequestOptions = {}
): Promise<GetMeResult> =>
  request('GET', '/api/v2/quartz/me', params, options) as Promise<GetMeResult>

export interface GetUserParams {
  userIdpeId: string
}

type GetUserResult =
  | GetUserOKResult
  | GetUserUnauthorizedResult
  | GetUserDefaultResult

interface GetUserOKResult {
  status: 200
  headers: Headers
  data: User
}

interface GetUserUnauthorizedResult {
  status: 401
  headers: Headers
  data: Error
}

interface GetUserDefaultResult {
  status: 500
  headers: Headers
  data: Error
}

export const getUser = (
  params: GetUserParams,
  options: RequestOptions = {}
): Promise<GetUserResult> =>
  request(
    'GET',
    `/api/v2/quartz/users/${params.userIdpeId}`,
    params,
    options
  ) as Promise<GetUserResult>

export interface DeleteAccountParams {
  data?: any
}

type DeleteAccountResult =
  | DeleteAccountNoContentResult
  | DeleteAccountUnauthorizedResult
  | DeleteAccountMethodNotAllowedResult
  | DeleteAccountDefaultResult

interface DeleteAccountNoContentResult {
  status: 204
  headers: Headers
  data: any
}

interface DeleteAccountUnauthorizedResult {
  status: 401
  headers: Headers
  data: Error
}

interface DeleteAccountMethodNotAllowedResult {
  status: 405
  headers: Headers
  data: Error
}

interface DeleteAccountDefaultResult {
  status: 500
  headers: Headers
  data: Error
}

export const deleteAccount = (
  params: DeleteAccountParams,
  options: RequestOptions = {}
): Promise<DeleteAccountResult> =>
  request(
    'DELETE',
    '/api/v2/quartz/account',
    {...params, headers: {'Content-Type': 'application/json; charset=utf-8'}},
    options
  ) as Promise<DeleteAccountResult>

export interface GetAccountsParams {}

type GetAccountsResult =
  | GetAccountsOKResult
  | GetAccountsUnauthorizedResult
  | GetAccountsDefaultResult

interface GetAccountsOKResult {
  status: 200
  headers: Headers
  data: UserAccount[]
}

interface GetAccountsUnauthorizedResult {
  status: 401
  headers: Headers
  data: Error
}

interface GetAccountsDefaultResult {
  status: 500
  headers: Headers
  data: Error
}

export const getAccounts = (
  params: GetAccountsParams,
  options: RequestOptions = {}
): Promise<GetAccountsResult> =>
  request('GET', '/api/v2/quartz/accounts', params, options) as Promise<
    GetAccountsResult
  >

export interface GetAccountParams {
  accountId: string

  headers?: {
    'Zap-Trace-Span'?: string
  }
}

type GetAccountResult =
  | GetAccountOKResult
  | GetAccountUnauthorizedResult
  | GetAccountDefaultResult

interface GetAccountOKResult {
  status: 200
  headers: Headers
  data: Account
}

interface GetAccountUnauthorizedResult {
  status: 401
  headers: Headers
  data: Error
}

interface GetAccountDefaultResult {
  status: 500
  headers: Headers
  data: Error
}

export const getAccount = (
  params: GetAccountParams,
  options: RequestOptions = {}
): Promise<GetAccountResult> =>
  request(
    'GET',
    `/api/v2/quartz/accounts/${params.accountId}`,
    params,
    options
  ) as Promise<GetAccountResult>

export interface PatchAccountParams {
  accountId: string

  data: {
    name: string
  }

  headers?: {
    'Zap-Trace-Span'?: string
  }
}

type PatchAccountResult =
  | PatchAccountOKResult
  | PatchAccountUnauthorizedResult
  | PatchAccountUnprocessableEntityResult
  | PatchAccountDefaultResult

interface PatchAccountOKResult {
  status: 200
  headers: Headers
  data: Account
}

interface PatchAccountUnauthorizedResult {
  status: 401
  headers: Headers
  data: Error
}

interface PatchAccountUnprocessableEntityResult {
  status: 422
  headers: Headers
  data: Error
}

interface PatchAccountDefaultResult {
  status: 500
  headers: Headers
  data: Error
}

export const patchAccount = (
  params: PatchAccountParams,
  options: RequestOptions = {}
): Promise<PatchAccountResult> =>
  request(
    'PATCH',
    `/api/v2/quartz/accounts/${params.accountId}`,
    {
      ...params,
      headers: {...params.headers, 'Content-Type': 'application/json'},
    },
    options
  ) as Promise<PatchAccountResult>

export interface GetAccountsOrgsParams {
  accountId: string

  headers?: {
    'Zap-Trace-Span'?: string
  }
}

type GetAccountsOrgsResult =
  | GetAccountsOrgsOKResult
  | GetAccountsOrgsUnauthorizedResult
  | GetAccountsOrgsDefaultResult

interface GetAccountsOrgsOKResult {
  status: 200
  headers: Headers
  data: OrganizationSummaries
}

interface GetAccountsOrgsUnauthorizedResult {
  status: 401
  headers: Headers
  data: Error
}

interface GetAccountsOrgsDefaultResult {
  status: 500
  headers: Headers
  data: Error
}

export const getAccountsOrgs = (
  params: GetAccountsOrgsParams,
  options: RequestOptions = {}
): Promise<GetAccountsOrgsResult> =>
  request(
    'GET',
    `/api/v2/quartz/accounts/${params.accountId}/orgs`,
    params,
    options
  ) as Promise<GetAccountsOrgsResult>

export interface PutAccountsOrgsDefaultParams {
  accountId: string

  data: OrganizationDefaultRequest
}

type PutAccountsOrgsDefaultResult =
  | PutAccountsOrgsDefaultNoContentResult
  | PutAccountsOrgsDefaultNotFoundResult
  | PutAccountsOrgsDefaultUnprocessableEntityResult
  | PutAccountsOrgsDefaultDefaultResult

interface PutAccountsOrgsDefaultNoContentResult {
  status: 204
  headers: Headers
  data: any
}

interface PutAccountsOrgsDefaultNotFoundResult {
  status: 404
  headers: Headers
  data: Error
}

interface PutAccountsOrgsDefaultUnprocessableEntityResult {
  status: 422
  headers: Headers
  data: Error
}

interface PutAccountsOrgsDefaultDefaultResult {
  status: 500
  headers: Headers
  data: Error
}

export const putAccountsOrgsDefault = (
  params: PutAccountsOrgsDefaultParams,
  options: RequestOptions = {}
): Promise<PutAccountsOrgsDefaultResult> =>
  request(
    'PUT',
    `/api/v2/quartz/accounts/${params.accountId}/orgs/default`,
    {...params, headers: {'Content-Type': 'application/json'}},
    options
  ) as Promise<PutAccountsOrgsDefaultResult>

export interface PutAccountsDefaultParams {
  data: {
    id: number
  }
}

type PutAccountsDefaultResult =
  | PutAccountsDefaultNoContentResult
  | PutAccountsDefaultDefaultResult

interface PutAccountsDefaultNoContentResult {
  status: 204
  headers: Headers
  data: any
}

interface PutAccountsDefaultDefaultResult {
  status: 500
  headers: Headers
  data: Error
}

export const putAccountsDefault = (
  params: PutAccountsDefaultParams,
  options: RequestOptions = {}
): Promise<PutAccountsDefaultResult> =>
  request(
    'PUT',
    '/api/v2/quartz/accounts/default',
    {...params, headers: {'Content-Type': 'application/json'}},
    options
  ) as Promise<PutAccountsDefaultResult>

export interface GetBillingParams {}

type GetBillingResult = GetBillingOKResult | GetBillingDefaultResult

interface GetBillingOKResult {
  status: 200
  headers: Headers
  data: BillingInfo
}

interface GetBillingDefaultResult {
  status: 500
  headers: Headers
  data: Error
}

export const getBilling = (
  params: GetBillingParams,
  options: RequestOptions = {}
): Promise<GetBillingResult> =>
  request('GET', '/api/v2/quartz/billing', params, options) as Promise<
    GetBillingResult
  >

export interface GetIdentityParams {}

type GetIdentityResult =
  | GetIdentityOKResult
  | GetIdentityUnauthorizedResult
  | GetIdentityDefaultResult

interface GetIdentityOKResult {
  status: 200
  headers: Headers
  data: Identity
}

interface GetIdentityUnauthorizedResult {
  status: 401
  headers: Headers
  data: Error
}

interface GetIdentityDefaultResult {
  status: 500
  headers: Headers
  data: Error
}

export const getIdentity = (
  params: GetIdentityParams,
  options: RequestOptions = {}
): Promise<GetIdentityResult> =>
  request('GET', '/api/v2/quartz/identity', params, options) as Promise<
    GetIdentityResult
  >

export interface GetMarketplaceParams {}

type GetMarketplaceResult =
  | GetMarketplaceOKResult
  | GetMarketplaceUnauthorizedResult
  | GetMarketplaceNotFoundResult
  | GetMarketplaceDefaultResult

interface GetMarketplaceOKResult {
  status: 200
  headers: Headers
  data: Marketplace
}

interface GetMarketplaceUnauthorizedResult {
  status: 401
  headers: Headers
  data: Error
}

interface GetMarketplaceNotFoundResult {
  status: 404
  headers: Headers
  data: Error
}

interface GetMarketplaceDefaultResult {
  status: 500
  headers: Headers
  data: Error
}

export const getMarketplace = (
  params: GetMarketplaceParams,
  options: RequestOptions = {}
): Promise<GetMarketplaceResult> =>
  request('GET', '/api/v2/quartz/marketplace', params, options) as Promise<
    GetMarketplaceResult
  >

export interface GetBillingStartDateParams {}

type GetBillingStartDateResult =
  | GetBillingStartDateOKResult
  | GetBillingStartDateDefaultResult

interface GetBillingStartDateOKResult {
  status: 200
  headers: Headers
  data: BillingDate
}

interface GetBillingStartDateDefaultResult {
  status: 500
  headers: Headers
  data: Error
}

export const getBillingStartDate = (
  params: GetBillingStartDateParams,
  options: RequestOptions = {}
): Promise<GetBillingStartDateResult> =>
  request(
    'GET',
    '/api/v2/quartz/billing/start_date',
    params,
    options
  ) as Promise<GetBillingStartDateResult>

export interface GetUsageVectorsParams {}

type GetUsageVectorsResult =
  | GetUsageVectorsOKResult
  | GetUsageVectorsUnauthorizedResult
  | GetUsageVectorsNotFoundResult
  | GetUsageVectorsDefaultResult

interface GetUsageVectorsOKResult {
  status: 200
  headers: Headers
  data: UsageVectors
}

interface GetUsageVectorsUnauthorizedResult {
  status: 401
  headers: Headers
  data: Error
}

interface GetUsageVectorsNotFoundResult {
  status: 404
  headers: Headers
  data: Error
}

interface GetUsageVectorsDefaultResult {
  status: 500
  headers: Headers
  data: Error
}

export const getUsageVectors = (
  params: GetUsageVectorsParams,
  options: RequestOptions = {}
): Promise<GetUsageVectorsResult> =>
  request('GET', '/api/v2/quartz/usage/vectors', params, options) as Promise<
    GetUsageVectorsResult
  >

export interface GetUsageParams {
  vector_name: any

  query?: {
    range?: any
  }
}

type GetUsageResult =
  | GetUsageOKResult
  | GetUsageBadRequestResult
  | GetUsageUnauthorizedResult
  | GetUsageNotFoundResult
  | GetUsageDefaultResult

interface GetUsageOKResult {
  status: 200
  headers: Headers
  data: string
}

interface GetUsageBadRequestResult {
  status: 400
  headers: Headers
  data: Error
}

interface GetUsageUnauthorizedResult {
  status: 401
  headers: Headers
  data: Error
}

interface GetUsageNotFoundResult {
  status: 404
  headers: Headers
  data: Error
}

interface GetUsageDefaultResult {
  status: 500
  headers: Headers
  data: Error
}

export const getUsage = (
  params: GetUsageParams,
  options: RequestOptions = {}
): Promise<GetUsageResult> =>
  request(
    'GET',
    `/api/v2/quartz/usage/${params.vector_name}`,
    params,
    options
  ) as Promise<GetUsageResult>

export interface GetUsageBillingStatsParams {}

type GetUsageBillingStatsResult =
  | GetUsageBillingStatsOKResult
  | GetUsageBillingStatsUnauthorizedResult
  | GetUsageBillingStatsDefaultResult

interface GetUsageBillingStatsOKResult {
  status: 200
  headers: Headers
  data: string
}

interface GetUsageBillingStatsUnauthorizedResult {
  status: 401
  headers: Headers
  data: Error
}

interface GetUsageBillingStatsDefaultResult {
  status: 500
  headers: Headers
  data: Error
}

export const getUsageBillingStats = (
  params: GetUsageBillingStatsParams,
  options: RequestOptions = {}
): Promise<GetUsageBillingStatsResult> =>
  request(
    'GET',
    '/api/v2/quartz/usage/billing_stats',
    params,
    options
  ) as Promise<GetUsageBillingStatsResult>

export interface GetUsageRateLimitsParams {
  query?: {
    range?: any
  }
}

type GetUsageRateLimitsResult =
  | GetUsageRateLimitsOKResult
  | GetUsageRateLimitsBadRequestResult
  | GetUsageRateLimitsUnauthorizedResult
  | GetUsageRateLimitsNotFoundResult
  | GetUsageRateLimitsDefaultResult

interface GetUsageRateLimitsOKResult {
  status: 200
  headers: Headers
  data: string
}

interface GetUsageRateLimitsBadRequestResult {
  status: 400
  headers: Headers
  data: Error
}

interface GetUsageRateLimitsUnauthorizedResult {
  status: 401
  headers: Headers
  data: Error
}

interface GetUsageRateLimitsNotFoundResult {
  status: 404
  headers: Headers
  data: Error
}

interface GetUsageRateLimitsDefaultResult {
  status: 500
  headers: Headers
  data: Error
}

export const getUsageRateLimits = (
  params: GetUsageRateLimitsParams,
  options: RequestOptions = {}
): Promise<GetUsageRateLimitsResult> =>
  request(
    'GET',
    '/api/v2/quartz/usage/rate_limits',
    params,
    options
  ) as Promise<GetUsageRateLimitsResult>

export interface GetSettingsNotificationsParams {}

type GetSettingsNotificationsResult =
  | GetSettingsNotificationsOKResult
  | GetSettingsNotificationsUnauthorizedResult
  | GetSettingsNotificationsNotFoundResult
  | GetSettingsNotificationsNotAcceptableResult
  | GetSettingsNotificationsDefaultResult

interface GetSettingsNotificationsOKResult {
  status: 200
  headers: Headers
  data: BillingNotifySettings
}

interface GetSettingsNotificationsUnauthorizedResult {
  status: 401
  headers: Headers
  data: Error
}

interface GetSettingsNotificationsNotFoundResult {
  status: 404
  headers: Headers
  data: Error
}

interface GetSettingsNotificationsNotAcceptableResult {
  status: 406
  headers: Headers
  data: Error
}

interface GetSettingsNotificationsDefaultResult {
  status: 500
  headers: Headers
  data: Error
}

export const getSettingsNotifications = (
  params: GetSettingsNotificationsParams,
  options: RequestOptions = {}
): Promise<GetSettingsNotificationsResult> =>
  request(
    'GET',
    '/api/v2/quartz/settings/notifications',
    params,
    options
  ) as Promise<GetSettingsNotificationsResult>

export interface PutSettingsNotificationsParams {
  data: BillingNotifySettings
}

type PutSettingsNotificationsResult =
  | PutSettingsNotificationsOKResult
  | PutSettingsNotificationsBadRequestResult
  | PutSettingsNotificationsUnauthorizedResult
  | PutSettingsNotificationsDefaultResult

interface PutSettingsNotificationsOKResult {
  status: 200
  headers: Headers
  data: BillingNotifySettings
}

interface PutSettingsNotificationsBadRequestResult {
  status: 400
  headers: Headers
  data: Error
}

interface PutSettingsNotificationsUnauthorizedResult {
  status: 401
  headers: Headers
  data: Error
}

interface PutSettingsNotificationsDefaultResult {
  status: 500
  headers: Headers
  data: Error
}

export const putSettingsNotifications = (
  params: PutSettingsNotificationsParams,
  options: RequestOptions = {}
): Promise<PutSettingsNotificationsResult> =>
  request(
    'PUT',
    '/api/v2/quartz/settings/notifications',
    {...params, headers: {'Content-Type': 'application/json'}},
    options
  ) as Promise<PutSettingsNotificationsResult>

export interface PutBillingContactParams {
  data: BillingContact
}

type PutBillingContactResult =
  | PutBillingContactOKResult
  | PutBillingContactDefaultResult

interface PutBillingContactOKResult {
  status: 200
  headers: Headers
  data: BillingContact
}

interface PutBillingContactDefaultResult {
  status: 500
  headers: Headers
  data: Error
}

export const putBillingContact = (
  params: PutBillingContactParams,
  options: RequestOptions = {}
): Promise<PutBillingContactResult> =>
  request(
    'PUT',
    '/api/v2/quartz/billing/contact',
    {...params, headers: {'Content-Type': 'application/json'}},
    options
  ) as Promise<PutBillingContactResult>

export interface GetPaymentFormParams {
  form: any
}

type GetPaymentFormResult =
  | GetPaymentFormOKResult
  | GetPaymentFormBadRequestResult
  | GetPaymentFormUnauthorizedResult
  | GetPaymentFormNotFoundResult
  | GetPaymentFormNotAcceptableResult
  | GetPaymentFormDefaultResult

interface GetPaymentFormOKResult {
  status: 200
  headers: Headers
  data: CreditCardParams
}

interface GetPaymentFormBadRequestResult {
  status: 400
  headers: Headers
  data: Error
}

interface GetPaymentFormUnauthorizedResult {
  status: 401
  headers: Headers
  data: Error
}

interface GetPaymentFormNotFoundResult {
  status: 404
  headers: Headers
  data: Error
}

interface GetPaymentFormNotAcceptableResult {
  status: 406
  headers: Headers
  data: Error
}

interface GetPaymentFormDefaultResult {
  status: 500
  headers: Headers
  data: Error
}

export const getPaymentForm = (
  params: GetPaymentFormParams,
  options: RequestOptions = {}
): Promise<GetPaymentFormResult> =>
  request(
    'GET',
    `/api/v2/quartz/payment_form/${params.form}`,
    params,
    options
  ) as Promise<GetPaymentFormResult>

export interface PutBillingPaymentMethodParams {
  data: PaymentMethodPut
}

type PutBillingPaymentMethodResult =
  | PutBillingPaymentMethodOKResult
  | PutBillingPaymentMethodDefaultResult

interface PutBillingPaymentMethodOKResult {
  status: 200
  headers: Headers
  data: PaymentMethod
}

interface PutBillingPaymentMethodDefaultResult {
  status: 500
  headers: Headers
  data: Error
}

export const putBillingPaymentMethod = (
  params: PutBillingPaymentMethodParams,
  options: RequestOptions = {}
): Promise<PutBillingPaymentMethodResult> =>
  request(
    'PUT',
    '/api/v2/quartz/billing/payment_method',
    {...params, headers: {'Content-Type': 'application/json'}},
    options
  ) as Promise<PutBillingPaymentMethodResult>

export interface GetBillingInvoicesParams {}

type GetBillingInvoicesResult =
  | GetBillingInvoicesOKResult
  | GetBillingInvoicesUnauthorizedResult
  | GetBillingInvoicesDefaultResult

interface GetBillingInvoicesOKResult {
  status: 200
  headers: Headers
  data: Invoices
}

interface GetBillingInvoicesUnauthorizedResult {
  status: 401
  headers: Headers
  data: Error
}

interface GetBillingInvoicesDefaultResult {
  status: 500
  headers: Headers
  data: Error
}

export const getBillingInvoices = (
  params: GetBillingInvoicesParams,
  options: RequestOptions = {}
): Promise<GetBillingInvoicesResult> =>
  request('GET', '/api/v2/quartz/billing/invoices', params, options) as Promise<
    GetBillingInvoicesResult
  >

export interface GetBillingInvoiceParams {
  invoiceId: string

  headers?: {
    'Zap-Trace-Span'?: string
  }
}

type GetBillingInvoiceResult =
  | GetBillingInvoiceOKResult
  | GetBillingInvoiceUnauthorizedResult
  | GetBillingInvoiceNotFoundResult
  | GetBillingInvoiceDefaultResult

interface GetBillingInvoiceOKResult {
  status: 200
  headers: Headers
  data: string
}

interface GetBillingInvoiceUnauthorizedResult {
  status: 401
  headers: Headers
  data: Error
}

interface GetBillingInvoiceNotFoundResult {
  status: 404
  headers: Headers
  data: Error
}

interface GetBillingInvoiceDefaultResult {
  status: 500
  headers: Headers
  data: Error
}

export const getBillingInvoice = (
  params: GetBillingInvoiceParams,
  options: RequestOptions = {}
): Promise<GetBillingInvoiceResult> =>
  request(
    'GET',
    `/api/v2/quartz/billing/invoices/${params.invoiceId}`,
    params,
    options
  ) as Promise<GetBillingInvoiceResult>

export interface GetOrgsParams {}

type GetOrgsResult =
  | GetOrgsOKResult
  | GetOrgsUnauthorizedResult
  | GetOrgsDefaultResult

interface GetOrgsOKResult {
  status: 200
  headers: Headers
  data: OrganizationSummaries
}

interface GetOrgsUnauthorizedResult {
  status: 401
  headers: Headers
  data: Error
}

interface GetOrgsDefaultResult {
  status: 500
  headers: Headers
  data: Error
}

export const getOrgs = (
  params: GetOrgsParams,
  options: RequestOptions = {}
): Promise<GetOrgsResult> =>
  request('GET', '/api/v2/quartz/orgs', params, options) as Promise<
    GetOrgsResult
  >

export interface GetOrgParams {
  orgId: string
}

type GetOrgResult =
  | GetOrgOKResult
  | GetOrgUnauthorizedResult
  | GetOrgDefaultResult

interface GetOrgOKResult {
  status: 200
  headers: Headers
  data: Organization
}

interface GetOrgUnauthorizedResult {
  status: 401
  headers: Headers
  data: Error
}

interface GetOrgDefaultResult {
  status: 500
  headers: Headers
  data: Error
}

export const getOrg = (
  params: GetOrgParams,
  options: RequestOptions = {}
): Promise<GetOrgResult> =>
  request(
    'GET',
    `/api/v2/quartz/orgs/${params.orgId}`,
    params,
    options
  ) as Promise<GetOrgResult>

export interface PatchOrgParams {
  orgId: string

  data: {
    name?: string
    description?: string
  }
}

type PatchOrgResult =
  | PatchOrgOKResult
  | PatchOrgUnauthorizedResult
  | PatchOrgUnprocessableEntityResult
  | PatchOrgDefaultResult

interface PatchOrgOKResult {
  status: 200
  headers: Headers
  data: Organization
}

interface PatchOrgUnauthorizedResult {
  status: 401
  headers: Headers
  data: Error
}

interface PatchOrgUnprocessableEntityResult {
  status: 422
  headers: Headers
  data: Error
}

interface PatchOrgDefaultResult {
  status: 500
  headers: Headers
  data: Error
}

export const patchOrg = (
  params: PatchOrgParams,
  options: RequestOptions = {}
): Promise<PatchOrgResult> =>
  request(
    'PATCH',
    `/api/v2/quartz/orgs/${params.orgId}`,
    {...params, headers: {'Content-Type': 'application/json'}},
    options
  ) as Promise<PatchOrgResult>

export interface PutOrgsDefaultParams {
  data: OrganizationDefaultRequest
}

type PutOrgsDefaultResult =
  | PutOrgsDefaultNoContentResult
  | PutOrgsDefaultNotFoundResult
  | PutOrgsDefaultUnprocessableEntityResult
  | PutOrgsDefaultDefaultResult

interface PutOrgsDefaultNoContentResult {
  status: 204
  headers: Headers
  data: any
}

interface PutOrgsDefaultNotFoundResult {
  status: 404
  headers: Headers
  data: Error
}

interface PutOrgsDefaultUnprocessableEntityResult {
  status: 422
  headers: Headers
  data: Error
}

interface PutOrgsDefaultDefaultResult {
  status: 500
  headers: Headers
  data: Error
}

export const putOrgsDefault = (
  params: PutOrgsDefaultParams,
  options: RequestOptions = {}
): Promise<PutOrgsDefaultResult> =>
  request(
    'PUT',
    '/api/v2/quartz/orgs/default',
    {...params, headers: {'Content-Type': 'application/json'}},
    options
  ) as Promise<PutOrgsDefaultResult>

export interface GetOrgsInvitesParams {
  orgId: string

  headers?: {
    'Zap-Trace-Span'?: string
  }
}

type GetOrgsInvitesResult =
  | GetOrgsInvitesOKResult
  | GetOrgsInvitesUnauthorizedResult
  | GetOrgsInvitesNotFoundResult
  | GetOrgsInvitesDefaultResult

interface GetOrgsInvitesOKResult {
  status: 200
  headers: Headers
  data: Invites
}

interface GetOrgsInvitesUnauthorizedResult {
  status: 401
  headers: Headers
  data: Error
}

interface GetOrgsInvitesNotFoundResult {
  status: 404
  headers: Headers
  data: Error
}

interface GetOrgsInvitesDefaultResult {
  status: 500
  headers: Headers
  data: Error
}

export const getOrgsInvites = (
  params: GetOrgsInvitesParams,
  options: RequestOptions = {}
): Promise<GetOrgsInvitesResult> =>
  request(
    'GET',
    `/api/v2/quartz/orgs/${params.orgId}/invites`,
    params,
    options
  ) as Promise<GetOrgsInvitesResult>

export interface PostOrgsInviteParams {
  orgId: string

  data: Invite

  headers?: {
    'Zap-Trace-Span'?: string
  }
}

type PostOrgsInviteResult =
  | PostOrgsInviteOKResult
  | PostOrgsInviteCreatedResult
  | PostOrgsInviteUnauthorizedResult
  | PostOrgsInviteNotFoundResult
  | PostOrgsInviteConflictResult
  | PostOrgsInviteUnprocessableEntityResult
  | PostOrgsInviteTooManyRequestsResult
  | PostOrgsInviteDefaultResult

interface PostOrgsInviteOKResult {
  status: 200
  headers: Headers
  data: OrgUser
}

interface PostOrgsInviteCreatedResult {
  status: 201
  headers: Headers
  data: Invite
}

interface PostOrgsInviteUnauthorizedResult {
  status: 401
  headers: Headers
  data: Error
}

interface PostOrgsInviteNotFoundResult {
  status: 404
  headers: Headers
  data: Error
}

interface PostOrgsInviteConflictResult {
  status: 409
  headers: Headers
  data: Error
}

interface PostOrgsInviteUnprocessableEntityResult {
  status: 422
  headers: Headers
  data: Error
}

interface PostOrgsInviteTooManyRequestsResult {
  status: 429
  headers: Headers
  data: Error
}

interface PostOrgsInviteDefaultResult {
  status: 500
  headers: Headers
  data: Error
}

export const postOrgsInvite = (
  params: PostOrgsInviteParams,
  options: RequestOptions = {}
): Promise<PostOrgsInviteResult> =>
  request(
    'POST',
    `/api/v2/quartz/orgs/${params.orgId}/invites`,
    {
      ...params,
      headers: {...params.headers, 'Content-Type': 'application/json'},
    },
    options
  ) as Promise<PostOrgsInviteResult>

export interface DeleteOrgsInviteParams {
  inviteId: number
  orgId: string

  data?: any

  headers?: {
    'Zap-Trace-Span'?: string
  }
}

type DeleteOrgsInviteResult =
  | DeleteOrgsInviteNoContentResult
  | DeleteOrgsInviteDefaultResult

interface DeleteOrgsInviteNoContentResult {
  status: 204
  headers: Headers
  data: any
}

interface DeleteOrgsInviteDefaultResult {
  status: 500
  headers: Headers
  data: Error
}

export const deleteOrgsInvite = (
  params: DeleteOrgsInviteParams,
  options: RequestOptions = {}
): Promise<DeleteOrgsInviteResult> =>
  request(
    'DELETE',
    `/api/v2/quartz/orgs/${params.orgId}/invites/${params.inviteId}`,
    {
      ...params,
      headers: {
        ...params.headers,
        'Content-Type': 'application/json; charset=utf-8',
      },
    },
    options
  ) as Promise<DeleteOrgsInviteResult>

export interface PostOrgsInvitesResendParams {
  inviteId: number
  orgId: string

  data?: any

  headers?: {
    'Zap-Trace-Span'?: string
  }
}

type PostOrgsInvitesResendResult =
  | PostOrgsInvitesResendOKResult
  | PostOrgsInvitesResendUnauthorizedResult
  | PostOrgsInvitesResendNotFoundResult
  | PostOrgsInvitesResendDefaultResult

interface PostOrgsInvitesResendOKResult {
  status: 200
  headers: Headers
  data: Invite
}

interface PostOrgsInvitesResendUnauthorizedResult {
  status: 401
  headers: Headers
  data: Error
}

interface PostOrgsInvitesResendNotFoundResult {
  status: 404
  headers: Headers
  data: Error
}

interface PostOrgsInvitesResendDefaultResult {
  status: 500
  headers: Headers
  data: Error
}

export const postOrgsInvitesResend = (
  params: PostOrgsInvitesResendParams,
  options: RequestOptions = {}
): Promise<PostOrgsInvitesResendResult> =>
  request(
    'POST',
    `/api/v2/quartz/orgs/${params.orgId}/invites/${params.inviteId}/resend`,
    {
      ...params,
      headers: {
        ...params.headers,
        'Content-Type': 'application/json; charset=utf-8',
      },
    },
    options
  ) as Promise<PostOrgsInvitesResendResult>

export interface GetOrgsUsersParams {
  orgId: string

  headers?: {
    'Zap-Trace-Span'?: string
  }
}

type GetOrgsUsersResult =
  | GetOrgsUsersOKResult
  | GetOrgsUsersUnauthorizedResult
  | GetOrgsUsersNotFoundResult
  | GetOrgsUsersDefaultResult

interface GetOrgsUsersOKResult {
  status: 200
  headers: Headers
  data: OrgUsers
}

interface GetOrgsUsersUnauthorizedResult {
  status: 401
  headers: Headers
  data: Error
}

interface GetOrgsUsersNotFoundResult {
  status: 404
  headers: Headers
  data: Error
}

interface GetOrgsUsersDefaultResult {
  status: 500
  headers: Headers
  data: Error
}

export const getOrgsUsers = (
  params: GetOrgsUsersParams,
  options: RequestOptions = {}
): Promise<GetOrgsUsersResult> =>
  request(
    'GET',
    `/api/v2/quartz/orgs/${params.orgId}/users`,
    params,
    options
  ) as Promise<GetOrgsUsersResult>

export interface DeleteOrgsUserParams {
  userId: string
  orgId: string

  data?: any

  headers?: {
    'Zap-Trace-Span'?: string
  }
}

type DeleteOrgsUserResult =
  | DeleteOrgsUserNoContentResult
  | DeleteOrgsUserBadRequestResult
  | DeleteOrgsUserUnauthorizedResult
  | DeleteOrgsUserNotFoundResult
  | DeleteOrgsUserDefaultResult

interface DeleteOrgsUserNoContentResult {
  status: 204
  headers: Headers
  data: any
}

interface DeleteOrgsUserBadRequestResult {
  status: 400
  headers: Headers
  data: Error
}

interface DeleteOrgsUserUnauthorizedResult {
  status: 401
  headers: Headers
  data: Error
}

interface DeleteOrgsUserNotFoundResult {
  status: 404
  headers: Headers
  data: Error
}

interface DeleteOrgsUserDefaultResult {
  status: 500
  headers: Headers
  data: Error
}

export const deleteOrgsUser = (
  params: DeleteOrgsUserParams,
  options: RequestOptions = {}
): Promise<DeleteOrgsUserResult> =>
  request(
    'DELETE',
    `/api/v2/quartz/orgs/${params.orgId}/users/${params.userId}`,
    {
      ...params,
      headers: {
        ...params.headers,
        'Content-Type': 'application/json; charset=utf-8',
      },
    },
    options
  ) as Promise<DeleteOrgsUserResult>

export interface GetOrgsLimitsParams {
  orgId: string
}

type GetOrgsLimitsResult = GetOrgsLimitsOKResult | GetOrgsLimitsDefaultResult

interface GetOrgsLimitsOKResult {
  status: 200
  headers: Headers
  data: OrgLimits
}

interface GetOrgsLimitsDefaultResult {
  status: 500
  headers: Headers
  data: Error
}

export const getOrgsLimits = (
  params: GetOrgsLimitsParams,
  options: RequestOptions = {}
): Promise<GetOrgsLimitsResult> =>
  request(
    'GET',
    `/api/v2/quartz/orgs/${params.orgId}/limits`,
    params,
    options
  ) as Promise<GetOrgsLimitsResult>

export interface PutOrgsLimitsParams {
  orgId: string

  data?: any
}

type PutOrgsLimitsResult = PutOrgsLimitsOKResult | PutOrgsLimitsDefaultResult

interface PutOrgsLimitsOKResult {
  status: 200
  headers: Headers
  data: OrgLimits
}

interface PutOrgsLimitsDefaultResult {
  status: 500
  headers: Headers
  data: Error
}

export const putOrgsLimits = (
  params: PutOrgsLimitsParams,
  options: RequestOptions = {}
): Promise<PutOrgsLimitsResult> =>
  request(
    'PUT',
    `/api/v2/quartz/orgs/${params.orgId}/limits`,
    {...params, headers: {'Content-Type': 'application/json; charset=utf-8'}},
    options
  ) as Promise<PutOrgsLimitsResult>

export interface PostCheckoutParams {
  data: CheckoutRequest
}

type PostCheckoutResult =
  | PostCheckoutCreatedResult
  | PostCheckoutBadRequestResult
  | PostCheckoutUnauthorizedResult
  | PostCheckoutNotFoundResult
  | PostCheckoutDefaultResult

interface PostCheckoutCreatedResult {
  status: 201
  headers: Headers
  data: any
}

interface PostCheckoutBadRequestResult {
  status: 400
  headers: Headers
  data: Error
}

interface PostCheckoutUnauthorizedResult {
  status: 401
  headers: Headers
  data: Error
}

interface PostCheckoutNotFoundResult {
  status: 404
  headers: Headers
  data: Error
}

interface PostCheckoutDefaultResult {
  status: 500
  headers: Headers
  data: Error
}

export const postCheckout = (
  params: PostCheckoutParams,
  options: RequestOptions = {}
): Promise<PostCheckoutResult> =>
  request(
    'POST',
    '/api/v2/quartz/checkout',
    {...params, headers: {'Content-Type': 'application/json'}},
    options
  ) as Promise<PostCheckoutResult>

export interface PostCancelParams {
  data?: any
}

type PostCancelResult = PostCancelNoContentResult | PostCancelDefaultResult

interface PostCancelNoContentResult {
  status: 204
  headers: Headers
  data: any
}

interface PostCancelDefaultResult {
  status: 500
  headers: Headers
  data: Error
}

export const postCancel = (
  params: PostCancelParams,
  options: RequestOptions = {}
): Promise<PostCancelResult> =>
  request(
    'POST',
    '/api/v2/quartz/cancel',
    {...params, headers: {'Content-Type': 'application/json; charset=utf-8'}},
    options
  ) as Promise<PostCancelResult>

export interface GetOperatorAccountsParams {
  query?: {
    query?: string
    accountTypes?: any
  }

  headers?: {
    'Zap-Trace-Span'?: string
  }
}

type GetOperatorAccountsResult =
  | GetOperatorAccountsOKResult
  | GetOperatorAccountsUnauthorizedResult
  | GetOperatorAccountsDefaultResult

interface GetOperatorAccountsOKResult {
  status: 200
  headers: Headers
  data: OperatorAccounts
}

interface GetOperatorAccountsUnauthorizedResult {
  status: 401
  headers: Headers
  data: Error
}

interface GetOperatorAccountsDefaultResult {
  status: 500
  headers: Headers
  data: Error
}

export const getOperatorAccounts = (
  params: GetOperatorAccountsParams,
  options: RequestOptions = {}
): Promise<GetOperatorAccountsResult> =>
  request(
    'GET',
    '/api/v2/quartz/operator/accounts',
    params,
    options
  ) as Promise<GetOperatorAccountsResult>

export interface GetOperatorAccountParams {
  accountId: string

  headers?: {
    'Zap-Trace-Span'?: string
  }
}

type GetOperatorAccountResult =
  | GetOperatorAccountOKResult
  | GetOperatorAccountUnauthorizedResult
  | GetOperatorAccountNotFoundResult
  | GetOperatorAccountDefaultResult

interface GetOperatorAccountOKResult {
  status: 200
  headers: Headers
  data: OperatorAccount & {
    organizations?: OperatorOrganizations
  } & any
}

interface GetOperatorAccountUnauthorizedResult {
  status: 401
  headers: Headers
  data: Error
}

interface GetOperatorAccountNotFoundResult {
  status: 404
  headers: Headers
  data: Error
}

interface GetOperatorAccountDefaultResult {
  status: 500
  headers: Headers
  data: Error
}

export const getOperatorAccount = (
  params: GetOperatorAccountParams,
  options: RequestOptions = {}
): Promise<GetOperatorAccountResult> =>
  request(
    'GET',
    `/api/v2/quartz/operator/accounts/${params.accountId}`,
    params,
    options
  ) as Promise<GetOperatorAccountResult>

export interface DeleteOperatorAccountParams {
  accountId: string

  data?: any

  headers?: {
    'Zap-Trace-Span'?: string
  }
}

type DeleteOperatorAccountResult =
  | DeleteOperatorAccountNoContentResult
  | DeleteOperatorAccountBadRequestResult
  | DeleteOperatorAccountUnauthorizedResult
  | DeleteOperatorAccountDefaultResult

interface DeleteOperatorAccountNoContentResult {
  status: 204
  headers: Headers
  data: any
}

interface DeleteOperatorAccountBadRequestResult {
  status: 400
  headers: Headers
  data: Error
}

interface DeleteOperatorAccountUnauthorizedResult {
  status: 401
  headers: Headers
  data: Error
}

interface DeleteOperatorAccountDefaultResult {
  status: 500
  headers: Headers
  data: Error
}

export const deleteOperatorAccount = (
  params: DeleteOperatorAccountParams,
  options: RequestOptions = {}
): Promise<DeleteOperatorAccountResult> =>
  request(
    'DELETE',
    `/api/v2/quartz/operator/accounts/${params.accountId}`,
    {
      ...params,
      headers: {
        ...params.headers,
        'Content-Type': 'application/json; charset=utf-8',
      },
    },
    options
  ) as Promise<DeleteOperatorAccountResult>

export interface PatchOperatorAccountsConvertParams {
  accountId: string

  data: {
    contractStartDate: string
  }

  headers?: {
    'Zap-Trace-Span'?: string
  }
}

type PatchOperatorAccountsConvertResult =
  | PatchOperatorAccountsConvertOKResult
  | PatchOperatorAccountsConvertUnauthorizedResult
  | PatchOperatorAccountsConvertUnprocessableEntityResult
  | PatchOperatorAccountsConvertDefaultResult

interface PatchOperatorAccountsConvertOKResult {
  status: 200
  headers: Headers
  data: OperatorAccount
}

interface PatchOperatorAccountsConvertUnauthorizedResult {
  status: 401
  headers: Headers
  data: Error
}

interface PatchOperatorAccountsConvertUnprocessableEntityResult {
  status: 422
  headers: Headers
  data: Error
}

interface PatchOperatorAccountsConvertDefaultResult {
  status: 500
  headers: Headers
  data: Error
}

export const patchOperatorAccountsConvert = (
  params: PatchOperatorAccountsConvertParams,
  options: RequestOptions = {}
): Promise<PatchOperatorAccountsConvertResult> =>
  request(
    'PATCH',
    `/api/v2/quartz/operator/accounts/${params.accountId}/convert`,
    {
      ...params,
      headers: {...params.headers, 'Content-Type': 'application/json'},
    },
    options
  ) as Promise<PatchOperatorAccountsConvertResult>

export interface DeleteOperatorAccountsUserParams {
  accountId: string
  userId: string

  data?: any

  headers?: {
    'Zap-Trace-Span'?: string
  }
}

type DeleteOperatorAccountsUserResult =
  | DeleteOperatorAccountsUserNoContentResult
  | DeleteOperatorAccountsUserUnauthorizedResult
  | DeleteOperatorAccountsUserDefaultResult

interface DeleteOperatorAccountsUserNoContentResult {
  status: 204
  headers: Headers
  data: any
}

interface DeleteOperatorAccountsUserUnauthorizedResult {
  status: 401
  headers: Headers
  data: Error
}

interface DeleteOperatorAccountsUserDefaultResult {
  status: 500
  headers: Headers
  data: Error
}

export const deleteOperatorAccountsUser = (
  params: DeleteOperatorAccountsUserParams,
  options: RequestOptions = {}
): Promise<DeleteOperatorAccountsUserResult> =>
  request(
    'DELETE',
    `/api/v2/quartz/operator/accounts/${params.accountId}/users/${params.userId}`,
    {
      ...params,
      headers: {
        ...params.headers,
        'Content-Type': 'application/json; charset=utf-8',
      },
    },
    options
  ) as Promise<DeleteOperatorAccountsUserResult>

export interface GetOperatorProvidersParams {}

type GetOperatorProvidersResult =
  | GetOperatorProvidersOKResult
  | GetOperatorProvidersUnauthorizedResult
  | GetOperatorProvidersDefaultResult

interface GetOperatorProvidersOKResult {
  status: 200
  headers: Headers
  data: OperatorProvidersResponse
}

interface GetOperatorProvidersUnauthorizedResult {
  status: 401
  headers: Headers
  data: Error
}

interface GetOperatorProvidersDefaultResult {
  status: 500
  headers: Headers
  data: Error
}

export const getOperatorProviders = (
  params: GetOperatorProvidersParams,
  options: RequestOptions = {}
): Promise<GetOperatorProvidersResult> =>
  request(
    'GET',
    '/api/v2/quartz/operator/providers',
    params,
    options
  ) as Promise<GetOperatorProvidersResult>

export interface GetOperatorOrgsParams {
  query?: {
    query?: string
    accountTypes?: any
    providers?: any
    regions?: any
  }

  headers?: {
    'Zap-Trace-Span'?: string
  }
}

type GetOperatorOrgsResult =
  | GetOperatorOrgsOKResult
  | GetOperatorOrgsUnauthorizedResult
  | GetOperatorOrgsDefaultResult

interface GetOperatorOrgsOKResult {
  status: 200
  headers: Headers
  data: OperatorOrganizations
}

interface GetOperatorOrgsUnauthorizedResult {
  status: 401
  headers: Headers
  data: Error
}

interface GetOperatorOrgsDefaultResult {
  status: 500
  headers: Headers
  data: Error
}

export const getOperatorOrgs = (
  params: GetOperatorOrgsParams,
  options: RequestOptions = {}
): Promise<GetOperatorOrgsResult> =>
  request('GET', '/api/v2/quartz/operator/orgs', params, options) as Promise<
    GetOperatorOrgsResult
  >

export interface GetOperatorOrgParams {
  orgId: string

  headers?: {
    'Zap-Trace-Span'?: string
  }
}

type GetOperatorOrgResult =
  | GetOperatorOrgOKResult
  | GetOperatorOrgUnauthorizedResult
  | GetOperatorOrgNotFoundResult
  | GetOperatorOrgDefaultResult

interface GetOperatorOrgOKResult {
  status: 200
  headers: Headers
  data: OperatorOrganization
}

interface GetOperatorOrgUnauthorizedResult {
  status: 401
  headers: Headers
  data: Error
}

interface GetOperatorOrgNotFoundResult {
  status: 404
  headers: Headers
  data: Error
}

interface GetOperatorOrgDefaultResult {
  status: 500
  headers: Headers
  data: Error
}

export const getOperatorOrg = (
  params: GetOperatorOrgParams,
  options: RequestOptions = {}
): Promise<GetOperatorOrgResult> =>
  request(
    'GET',
    `/api/v2/quartz/operator/orgs/${params.orgId}`,
    params,
    options
  ) as Promise<GetOperatorOrgResult>

export interface GetOperatorOrgsLimitsParams {
  orgId: string

  headers?: {
    'Zap-Trace-Span'?: string
  }
}

type GetOperatorOrgsLimitsResult =
  | GetOperatorOrgsLimitsOKResult
  | GetOperatorOrgsLimitsUnauthorizedResult
  | GetOperatorOrgsLimitsNotFoundResult
  | GetOperatorOrgsLimitsDefaultResult

interface GetOperatorOrgsLimitsOKResult {
  status: 200
  headers: Headers
  data: OperatorOrgLimits
}

interface GetOperatorOrgsLimitsUnauthorizedResult {
  status: 401
  headers: Headers
  data: Error
}

interface GetOperatorOrgsLimitsNotFoundResult {
  status: 404
  headers: Headers
  data: Error
}

interface GetOperatorOrgsLimitsDefaultResult {
  status: 500
  headers: Headers
  data: Error
}

export const getOperatorOrgsLimits = (
  params: GetOperatorOrgsLimitsParams,
  options: RequestOptions = {}
): Promise<GetOperatorOrgsLimitsResult> =>
  request(
    'GET',
    `/api/v2/quartz/operator/orgs/${params.orgId}/limits`,
    params,
    options
  ) as Promise<GetOperatorOrgsLimitsResult>

export interface PutOperatorOrgsLimitsParams {
  orgId: string

  data: OperatorOrgLimits

  headers?: {
    'Zap-Trace-Span'?: string
  }
}

type PutOperatorOrgsLimitsResult =
  | PutOperatorOrgsLimitsOKResult
  | PutOperatorOrgsLimitsBadRequestResult
  | PutOperatorOrgsLimitsUnauthorizedResult
  | PutOperatorOrgsLimitsNotFoundResult
  | PutOperatorOrgsLimitsDefaultResult

interface PutOperatorOrgsLimitsOKResult {
  status: 200
  headers: Headers
  data: OperatorOrgLimits
}

interface PutOperatorOrgsLimitsBadRequestResult {
  status: 400
  headers: Headers
  data: Error
}

interface PutOperatorOrgsLimitsUnauthorizedResult {
  status: 401
  headers: Headers
  data: Error
}

interface PutOperatorOrgsLimitsNotFoundResult {
  status: 404
  headers: Headers
  data: Error
}

interface PutOperatorOrgsLimitsDefaultResult {
  status: 500
  headers: Headers
  data: Error
}

export const putOperatorOrgsLimits = (
  params: PutOperatorOrgsLimitsParams,
  options: RequestOptions = {}
): Promise<PutOperatorOrgsLimitsResult> =>
  request(
    'PUT',
    `/api/v2/quartz/operator/orgs/${params.orgId}/limits`,
    {
      ...params,
      headers: {...params.headers, 'Content-Type': 'application/json'},
    },
    options
  ) as Promise<PutOperatorOrgsLimitsResult>

export interface GetAuthConnectionParams {
  query: {
    email: string
  }

  headers?: {
    'Zap-Trace-Span'?: string
  }
}

type GetAuthConnectionResult =
  | GetAuthConnectionOKResult
  | GetAuthConnectionNoContentResult
  | GetAuthConnectionDefaultResult

interface GetAuthConnectionOKResult {
  status: 200
  headers: Headers
  data: string
}

interface GetAuthConnectionNoContentResult {
  status: 204
  headers: Headers
  data: any
}

interface GetAuthConnectionDefaultResult {
  status: 500
  headers: Headers
  data: Error
}

export const getAuthConnection = (
  params: GetAuthConnectionParams,
  options: RequestOptions = {}
): Promise<GetAuthConnectionResult> =>
  request('GET', '/api/v2/quartz/auth/connection', params, options) as Promise<
    GetAuthConnectionResult
  >
